import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@xxtract/ui';
import apiClient from '@/lib/api-client';

type ProductImageVariant = 'thumbnail' | 'web';

interface ProductImageProps {
  gln: string;
  gtin: string;
  productName: string;
  targetMarket?: string;
  variant: ProductImageVariant;
}

interface DescriptorCacheEntry { expiresAt: number; url: string; }
const descriptorCache = new Map<string, DescriptorCacheEntry>();
const descriptorInFlight = new Map<string, Promise<string>>();
const CLOCK_SKEW_MS = 5_000;

export function productImageDescriptorUrl(gln: string, gtin: string, variant: ProductImageVariant, targetMarket = '528') {
  return `/media/trade-items/${encodeURIComponent(gln)}/${encodeURIComponent(gtin)}/${encodeURIComponent(targetMarket)}/image/${variant}/descriptor`;
}

function mintDescriptor(endpoint: string): Promise<string> {
  const cached = descriptorCache.get(endpoint);
  if (cached != null && cached.expiresAt > Date.now()) return Promise.resolve(cached.url);
  const pending = descriptorInFlight.get(endpoint);
  if (pending != null) return pending;
  const request = apiClient.get<{ data: { url: string; expiresAt: number } }>(endpoint).then((response) => {
    const url = response.data.data.url;
    // Cap local reuse beneath the server's short capability TTL; a stale URL is
    // never retried by the image error handler.
    const expiresAt = response.data.data.expiresAt * 1000 - CLOCK_SKEW_MS;
    if (!Number.isSafeInteger(response.data.data.expiresAt) || expiresAt <= Date.now()) throw new Error('Descriptor is already expired');
    descriptorCache.set(endpoint, { url, expiresAt });
    return url;
  });
  descriptorInFlight.set(endpoint, request);
  void request.then(
    () => { if (descriptorInFlight.get(endpoint) === request) descriptorInFlight.delete(endpoint); },
    () => { if (descriptorInFlight.get(endpoint) === request) descriptorInFlight.delete(endpoint); },
  );
  return request;
}

function Placeholder({ variant, label }: { variant: ProductImageVariant; label: string }) {
  return <div aria-label={label} className={variant === 'thumbnail' ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]' : 'flex h-40 w-40 items-center justify-center rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]'} role="img"><svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24"><rect height="16" rx="2" stroke="currentColor" strokeWidth="1.5" width="18" x="3" y="4" /><circle cx="8.5" cy="9" fill="currentColor" r="1.5" /><path d="m4 18 5-5 3 3 3-3 5 5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" /></svg></div>;
}

export function ProductImage({ gln, gtin, productName, targetMarket = '528', variant }: ProductImageProps) {
  const { t } = useTranslation();
  const endpoint = useMemo(() => productImageDescriptorUrl(gln, gtin, variant, targetMarket), [gln, gtin, targetMarket, variant]);
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const remintAttempted = useRef(false);
  const alt = t('products.media.alt', { productName });
  const placeholder = t('products.media.placeholder');

  useEffect(() => {
    let active = true;
    setSrc(null);
    setFailed(false);
    setOpen(false);
    remintAttempted.current = false;
    if (!gln || !gtin) return () => { active = false; };
    void mintDescriptor(endpoint).then((url) => { if (active) setSrc(url); }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [endpoint, gln, gtin]);

  const handleImageError = () => {
    setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
    if (remintAttempted.current) {
      setFailed(true);
      return;
    }
    remintAttempted.current = true;
    descriptorCache.delete(endpoint);
    setSrc(null);
    void mintDescriptor(endpoint).then(setSrc).catch(() => setFailed(true));
  };
  const image = !failed && src != null ? <img alt={alt} className={variant === 'thumbnail' ? 'h-10 w-10 shrink-0 rounded-[var(--radius-control)] border border-[var(--border)] object-contain' : 'h-40 w-40 rounded-[var(--radius-card)] border border-[var(--border)] object-contain'} onError={handleImageError} src={src} /> : <Placeholder label={placeholder} variant={variant} />;

  if (variant === 'thumbnail') return image;
  return <Dialog onOpenChange={setOpen} open={open}><DialogTrigger><button aria-label={t('products.media.open', { productName })} className="rounded-[var(--radius-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]" ref={triggerRef} type="button">{image}</button></DialogTrigger><DialogContent className="max-w-3xl"><DialogHeader><DialogTitle>{t('products.media.dialogTitle', { productName })}</DialogTitle></DialogHeader>{!failed && src != null ? <img alt={alt} className="max-h-[60vh] w-full object-contain" onError={handleImageError} src={src} /> : <Placeholder label={placeholder} variant="web" />}<DialogClose><Button variant="outline">{t('common.close')}</Button></DialogClose></DialogContent></Dialog>;
}
